import type {
  Card,
  GameType,
  RoundCardOwner,
  RoundEntry,
  RoundEntryOutcome,
} from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';

/**
 * The pure view-model behind the owner's round-history drill-down: a member's
 * settled rounds as the console reads them back. Derived from the REST
 * entries alone — no React, no fetching.
 */

export interface RoundBetVM {
  label: string;
  /** The wager itself — "Pass line" out of "Pass line — loss". */
  name: string;
  /** How that wager resolved, when the game printed it into the label. */
  resolution: string | null;
  netLabel: string;
  tone: 'win' | 'loss' | 'even';
}

/** One hand of cards the round dealt, ready for mini cards. */
export interface RoundHandVM {
  key: string;
  ownerLabel: string;
  cards: Card[];
  /** A total or a poker hand name. */
  label: string | null;
}

export interface RoundRowVM {
  key: string;
  gameType: GameType;
  /** A one-character badge for the game. */
  glyph: string;
  gameLabel: string;
  outcomeLabel: string;
  tone: 'win' | 'loss' | 'push';
  netLabel: string;
  /** "240 staked" — everything the round cost before the returns came in. */
  stakeLabel: string;
  /** "480 back" — everything returned, stakes included. */
  returnLabel: string;
  stakeChips: string;
  returnChips: string;
  /** Stake and return against the larger of the two, 0..1, for the flow bars. */
  stakeFrac: number;
  returnFrac: number;
  /** The round's headline: the pocket, the dice, the showdown hand. */
  note: string | null;
  /** Local clock, HH:MM — the night runs in one room and one timezone. */
  whenLabel: string;
  hands: RoundHandVM[];
  /** Craps: the last roll, read back off the note. */
  dice: [number, number] | null;
  /** Roulette: the pocket that hit, read back off the note. */
  pocket: { label: string; color: 'red' | 'black' | 'green' } | null;
  bets: RoundBetVM[];
}

export interface MemberHistoryVM {
  rows: RoundRowVM[];
  hasMore: boolean;
}

export const GAME_LABELS: Record<GameType, string> = {
  blackjack: 'Blackjack',
  holdem: "Hold'em",
  baccarat: 'Baccarat',
  roulette: 'Roulette',
  craps: 'Craps',
  threecard: '3-Card Poker',
  keno: 'Keno',
};

export const GAME_GLYPHS: Record<GameType, string> = {
  blackjack: '♠',
  holdem: '♣',
  baccarat: '♦',
  roulette: '◎',
  craps: '⚄',
  threecard: '♥',
  keno: '#',
};

const OUTCOME_LABELS: Record<RoundEntryOutcome, string> = {
  blackjack: 'Blackjack',
  win: 'Win',
  push: 'Push',
  loss: 'Loss',
  surrender: 'Surrender',
  fold: 'Fold',
};

const OWNER_LABELS: Record<RoundCardOwner, string> = {
  player: 'You',
  dealer: 'Dealer',
  board: 'Board',
  banker: 'Banker',
};

const clockLabel = (iso: string): string => {
  const at = new Date(iso);
  const two = (n: number): string => String(n).padStart(2, '0');
  return `${two(at.getHours())}:${two(at.getMinutes())}`;
};

/** "Seven out — 5 rolls, last 3-4" → [3, 4]. */
export function diceOf(note: string | null): [number, number] | null {
  const m = note?.match(/last ([1-6])-([1-6])$/);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/** "Pocket 17 (red)" → { label: '17', color: 'red' }. */
export function pocketOf(note: string | null): RoundRowVM['pocket'] {
  const m = note?.match(/^Pocket (\d+) \((red|black|green)\)$/i);
  if (!m) return null;
  return { label: m[1]!, color: m[2]!.toLowerCase() as 'red' | 'black' | 'green' };
}

export function memberHistoryModel(
  entries: readonly RoundEntry[],
  hasMore = false,
): MemberHistoryVM {
  const rows = entries.map((entry): RoundRowVM => {
    const outcome = entry.outcome;
    const scale = Math.max(entry.stake, entry.payout, 1);
    const isCraps = entry.gameType === 'craps';
    const isRoulette = entry.gameType === 'roulette';
    return {
      key: entry.id,
      gameType: entry.gameType,
      glyph: GAME_GLYPHS[entry.gameType] ?? '•',
      gameLabel: GAME_LABELS[entry.gameType] ?? entry.gameType,
      outcomeLabel: OUTCOME_LABELS[outcome] ?? outcome,
      tone:
        outcome === 'win' || outcome === 'blackjack'
          ? 'win'
          : outcome === 'push'
            ? 'push'
            : 'loss',
      netLabel: formatSignedChips(entry.net),
      stakeLabel: `${entry.stake.toLocaleString()} staked`,
      returnLabel: `${entry.payout.toLocaleString()} back`,
      stakeChips: entry.stake.toLocaleString(),
      returnChips: entry.payout.toLocaleString(),
      stakeFrac: entry.stake / scale,
      returnFrac: entry.payout / scale,
      note: entry.note,
      whenLabel: clockLabel(entry.at),
      hands: entry.cards
        .filter((hand) => hand.cards.length > 0)
        .map((hand, i) => ({
          key: `${hand.owner}-${i}`,
          // Baccarat's "player" is the Player spot, not the member.
          ownerLabel:
            hand.owner === 'player' && entry.gameType === 'baccarat'
              ? 'Player'
              : OWNER_LABELS[hand.owner] ?? hand.owner,
          cards: hand.cards,
          label: hand.label,
        })),
      dice: isCraps ? diceOf(entry.note) : null,
      pocket: isRoulette ? pocketOf(entry.note) : null,
      bets: entry.bets.map((bet) => {
        const net = bet.payout - bet.stake;
        const split = bet.label.indexOf(' — ');
        return {
          label: bet.label,
          name: split === -1 ? bet.label : bet.label.slice(0, split),
          resolution: split === -1 ? null : bet.label.slice(split + 3),
          netLabel: formatSignedChips(net),
          tone: net > 0 ? 'win' : net < 0 ? 'loss' : 'even',
        };
      }),
    };
  });
  return { rows, hasMore };
}
